import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import axios, { AxiosError } from "axios";

import DevClient from "./DevClient";

export type APIClientInitOptions = {
  baseURL: string;
  baseOptions?: AxiosRequestConfig;
  version?: string;
  language?: string | null;
  secret: string;
  onApiError?: (error: AxiosError) => void;
};

export type APIInstance = AxiosInstance;

export type APIClient = {
  init: (options: APIClientInitOptions) => void;
  getClient: () => APIInstance;
  setLanguage: (lang: string | null) => void;
  addOnAPIErrorListener: (listener: (error: AxiosError) => void) => {
    remove: () => void;
  };
  addOnAPIResponseListener: (listener: (response: AxiosResponse) => void) => {
    remove: () => void;
  };
  fetch: <D, R>(
    fn: (instance: APIInstance, data: D) => Promise<R>,
  ) => (data: D) => Promise<R>;
};

class MockError extends Error {
  url: string;
  config: AxiosRequestConfig;

  constructor(url: string, config: AxiosRequestConfig) {
    super("MOCK_ERROR");
    this.url = url;
    this.config = config;
  }
}

const createAPIClient = (options: APIClientInitOptions): APIClient => {
  let instance: AxiosInstance | null = null;
  let language: string | null = null;
  let refreshingToken: Promise<string | null> | null = null;
  let accessToken: string | null = null;

  const onApiErrorListeners: ((error: AxiosError) => void)[] = [];
  const onApiResponseListeners: ((error: AxiosResponse) => void)[] = [];

  function addOnAPIErrorListener(listener: (error: AxiosError) => void) {
    onApiErrorListeners.push(listener);
    return {
      remove: () => removeOnAPIErrorListener(listener),
    };
  }

  function removeOnAPIErrorListener(listener: (error: AxiosError) => void) {
    const index = onApiErrorListeners.indexOf(listener);
    if (index >= 0) {
      onApiErrorListeners.splice(index, 1);
    }
  }

  function notifyOnAPIErrorListeners(error: AxiosError) {
    onApiErrorListeners.forEach((listener) => listener(error));
  }

  function addOnAPIResponseListener(listener: (res: AxiosResponse) => void) {
    onApiResponseListeners.push(listener);
    return {
      remove: () => removeOnAPIResponseListener(listener),
    };
  }

  function removeOnAPIResponseListener(listener: (res: AxiosResponse) => void) {
    const index = onApiResponseListeners.indexOf(listener);
    if (index >= 0) {
      onApiResponseListeners.splice(index, 1);
    }
  }

  function notifyOnAPIResponseListeners(res: AxiosResponse) {
    onApiResponseListeners.forEach((listener) => listener(res));
  }

  function init(clientOptions: APIClientInitOptions) {
    if (clientOptions.language) {
      language = clientOptions.language;
    }
    if (clientOptions.onApiError) {
      addOnAPIErrorListener(clientOptions.onApiError);
    }
    instance = axios.create({
      baseURL: clientOptions.baseURL,
      headers: {
        "secret-key": clientOptions.secret,
      },
      ...clientOptions.baseOptions,
    });
    instance.interceptors.request.use(function (config) {
      if (config.url?.includes("FIXME")) {
        return Promise.reject(new MockError(config.url, config));
      }
      return {
        ...config,
        headers: Object.assign(config.headers, {
          "Accept-Language": language || "cs",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        }),
      };
    });
    instance.interceptors.response.use(
      (res) => {
        notifyOnAPIResponseListeners(res);
        return res;
      },
      async (error) => {
        if (error instanceof MockError) {
          return DevClient.get(error.config.url || "", error.config);
        }

        if (error instanceof AxiosError) {
          // Attempt token refresh if 401, except for auth endpoints to avoid loops.
          const original = error.config as
            | (InternalAxiosRequestConfig & { _retry?: boolean })
            | undefined;
          const isAuthEndpoint = original?.url?.includes("/account/web/");
          if (
            error.response?.status === 401 &&
            original &&
            !original._retry &&
            !isAuthEndpoint
          ) {
            original._retry = true;
            refreshingToken ??= refreshAccessToken().finally(() => {
              refreshingToken = null;
            });
            const token = await refreshingToken;
            if (token && instance) {
              original.headers.set("Authorization", `Bearer ${token}`);
              return instance(original);
            }
          }

          notifyOnAPIErrorListeners(error);
        }
        return Promise.reject(error);
      },
    );
  }

  function setLanguage(lang: string | null) {
    language = lang;
  }

  function getClient() {
    if (!instance) {
      throw new Error("Client was not initialized");
    }
    return instance;
  }

  async function refreshAccessToken(): Promise<string | null> {
    try {
      const res = await getClient().post<{ token: string }>(
        `/account/web/refresh`,
        {},
        { withCredentials: true },
      );
      accessToken = res.data.token;
      return res.data.token;
    } catch {
      accessToken = null;
      return null;
    }
  }

  init(options);

  return {
    init,
    getClient,
    setLanguage,
    addOnAPIErrorListener,
    addOnAPIResponseListener,
    fetch:
      <D, R>(fn: (instance: APIInstance, data: D) => Promise<R>) =>
      (data: D) =>
        fn(getClient(), data),
  };
};

export default createAPIClient;
